import { useNavigate, useParams, useRouterState } from "@tanstack/react-router";

import { NAVIGATION_PATHS } from "@constants/navigation";

import { useCaseByCaseNumber } from "@hooks/queries/use-cases";

import { buildCaseSubtitle } from "./build-case-subtitle";
import { CASE_TABS } from "./constants";

export const useCaseDetailTabs = () => {
  const { id: caseNumber, templateId } = useParams({ strict: false }) as {
    id: string;
    templateId?: string;
  };
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { data: caseData, isLoading: isCaseLoading } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  const activeTab =
    CASE_TABS.find((tab) => pathname.includes(`/${tab.id}`))?.id ?? "";

  const isOnReportPreview = pathname.endsWith("/preview");

  const handleTabChange = (tabId: string) => {
    navigate({ to: `${NAVIGATION_PATHS.REPORT_STUDIO}/${caseNumber}/${tabId}` });
  };

  const caseTitle = `Examinee Name: ${caseData?.examinee_name ?? ""} - Claim: ${caseData?.claim_number ?? ""}`;

  // Bulleted subtitle line rendered under the breadcrumb, e.g.
  // "Examinee: Samantha Jones • Claim: 123456789 • Workers Compensation".
  const caseSubtitle = buildCaseSubtitle({
    examineeName: caseData?.examinee_name,
    claimNumber: caseData?.claim_number,
    serviceType: caseData?.service_type,
  });

  const isOnOverview = activeTab === "";

  const breadcrumbs: Array<{ label: string; href?: string }> = [
    { label: "Report Studio", href: NAVIGATION_PATHS.REPORT_STUDIO },
  ];

  breadcrumbs.push({
    label: caseTitle,
    href: isOnOverview
      ? undefined
      : `${NAVIGATION_PATHS.REPORT_STUDIO}/${caseNumber}`,
  });

  if (activeTab) {
    const activeTabLabel = CASE_TABS.find((tab) => tab.id === activeTab)?.label;
    if (activeTabLabel) {
      const activeTabHref =
        isOnReportPreview && activeTab === "report-builder" && templateId
          ? `${NAVIGATION_PATHS.REPORT_STUDIO}/${caseNumber}/report-builder/template/${templateId}`
          : undefined;
      breadcrumbs.push({ label: activeTabLabel, href: activeTabHref });
    }
    if (isOnReportPreview) {
      breadcrumbs.push({ label: "Report Preview" });
    }
  } else {
    breadcrumbs.push({ label: "Case Detail" });
  }

  return {
    activeTab,
    breadcrumbs,
    caseTitle,
    caseSubtitle,
    isCaseLoading,
    isOnOverview,
    handleTabChange,
  };
};
